import { useState, useCallback, useEffect, useRef } from 'react';
import { Contract, JsonRpcProvider } from 'ethers';
import { CONTRACT_ADDRESS } from '../constants';

// Extended ABI for gallery queries
// Note: Remove 'public' keyword for ethers.js compatibility
const GALLERY_ABI = [
  "function totalSupply() view returns (uint256)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function ownerOf(uint256 tokenId) view returns (address)",
];

// Public RPC endpoints for Base Mainnet (with fallbacks)
const BASE_RPC_ENDPOINTS = [
  "https://mainnet.base.org",
  "https://base-rpc.publicnode.com",
  "https://base.gateway.tenderly.co",
];

// Cached provider instance to reuse connections
let cachedProvider = null;
let providerPromise = null;

// Try RPC endpoints with fallback and caching
const getProvider = async () => {
  // Return cached provider if available
  if (cachedProvider) {
    try {
      // Verify cached provider is still working
      await Promise.race([
        cachedProvider.getBlockNumber(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 3000)
        ),
      ]);
      return cachedProvider;
    } catch (err) {
      console.warn('Cached provider failed, reconnecting...', err.message);
      cachedProvider = null;
    }
  }

  // Reuse in-flight connection attempt
  if (providerPromise) {
    return providerPromise;
  }

  // Create new provider connection
  providerPromise = (async () => {
    let lastError;

    for (const rpc of BASE_RPC_ENDPOINTS) {
      try {
        console.log(`Attempting to connect to RPC: ${rpc}`);
        const provider = new JsonRpcProvider(rpc, 8453, {
          staticNetwork: true, // Skip network detection
        });

        // Test connection with timeout
        const blockNumber = await Promise.race([
          provider.getBlockNumber(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 8000)
          ),
        ]);

        console.log(`✅ Connected to ${rpc}, block: ${blockNumber}`);
        cachedProvider = provider;
        providerPromise = null;
        return provider;
      } catch (err) {
        console.warn(`❌ RPC ${rpc} failed:`, err.message);
        lastError = err;
        continue;
      }
    }

    providerPromise = null;
    throw new Error(`All RPC endpoints failed. Last error: ${lastError?.message}`);
  })();

  return providerPromise;
};

// Convert IPFS URI to HTTP gateway URL
const ipfsToHttp = (uri) => {
  if (!uri) return null;
  if (uri.startsWith('ipfs://')) {
    return uri.replace('ipfs://', 'https://gateway.pinata.cloud/ipfs/');
  }
  return uri;
};

export const useGallery = () => {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalSupply, setTotalSupply] = useState(0);
  const abortControllerRef = useRef(null);

  const fetchNFTs = useCallback(async (limit = 20) => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);

    try {
      // Check if request was aborted
      if (signal.aborted) {
        return;
      }

      const provider = await getProvider();

      if (signal.aborted) {
        return;
      }

      const contract = new Contract(CONTRACT_ADDRESS, GALLERY_ABI, provider);

      // Check if contract exists by trying to get code
      const code = await provider.getCode(CONTRACT_ADDRESS);

      if (signal.aborted) {
        return;
      }

      if (code === '0x') {
        // Contract doesn't exist at this address
        setError('No NFT contract found. Have you deployed the contract yet?');
        setNfts([]);
        setTotalSupply(0);
        setLoading(false);
        return;
      }

      // Get total supply
      const supply = await contract.totalSupply();

      if (signal.aborted) {
        return;
      }

      const supplyNum = Number(supply);
      setTotalSupply(supplyNum);

      if (supplyNum === 0) {
        // Contract exists but no NFTs minted yet
        setNfts([]);
        setLoading(false);
        return;
      }

      // Fetch the most recent NFTs (up to limit)
      const startId = Math.max(0, supplyNum - limit);
      const nftPromises = [];

      for (let i = supplyNum - 1; i >= startId; i--) {
        nftPromises.push(fetchSingleNFT(contract, i, signal));
      }

      const fetchedNfts = await Promise.all(nftPromises);

      if (signal.aborted) {
        return;
      }

      setNfts(fetchedNfts.filter(nft => nft !== null));
    } catch (err) {
      // Ignore errors from aborted requests
      if (err.name === 'AbortError' || signal.aborted) {
        console.log('Request was cancelled');
        return;
      }

      console.error('Failed to fetch NFTs:', err);

      // Provide more helpful error messages
      if (err.code === 'CALL_EXCEPTION') {
        setError('Unable to connect to the NFT contract. Please check your network connection or try again later.');
      } else if (err.message.includes('network')) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError(err.message || 'Failed to load NFTs');
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const fetchSingleNFT = async (contract, tokenId, signal) => {
    try {
      if (signal?.aborted) {
        return null;
      }

      const [tokenURI, owner] = await Promise.all([
        contract.tokenURI(tokenId),
        contract.ownerOf(tokenId),
      ]);

      if (signal?.aborted) {
        return null;
      }

      // Fetch metadata from IPFS
      const metadataUrl = ipfsToHttp(tokenURI);
      const metadataResponse = await fetch(metadataUrl, {
        signal: signal, // Pass abort signal to fetch
      });

      if (!metadataResponse.ok) {
        throw new Error('Failed to fetch metadata');
      }

      const metadata = await metadataResponse.json();

      return {
        tokenId,
        owner,
        tokenURI,
        metadata,
        imageUrl: ipfsToHttp(metadata.image),
        name: metadata.name,
        description: metadata.description,
        attributes: metadata.attributes || [],
      };
    } catch (err) {
      console.error(`Failed to fetch NFT #${tokenId}:`, err);
      return null;
    }
  };

  // Fetch NFTs for a specific owner
  const fetchNFTsByOwner = useCallback(async (ownerAddress, limit = 50) => {
    console.log('fetchNFTsByOwner called with address:', ownerAddress);

    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);

    try {
      if (signal.aborted) {
        return;
      }

      const provider = await getProvider();
      console.log('✅ Provider obtained for fetchNFTsByOwner');

      if (signal.aborted) {
        return;
      }

      const contract = new Contract(CONTRACT_ADDRESS, GALLERY_ABI, provider);

      const supply = await contract.totalSupply();
      console.log('Total supply:', supply.toString());

      if (signal.aborted) {
        return;
      }

      const supplyNum = Number(supply);
      setTotalSupply(supplyNum);

      if (supplyNum === 0) {
        setNfts([]);
        setLoading(false);
        return;
      }

      // Check ownership for each token (this is inefficient but works without indexer)
      const checkPromises = [];

      for (let i = 0; i < supplyNum; i++) {
        checkPromises.push(
          (async () => {
            try {
              if (signal.aborted) {
                return null;
              }

              const owner = await contract.ownerOf(i);

              if (signal.aborted) {
                return null;
              }

              const isOwned = owner.toLowerCase() === ownerAddress.toLowerCase();
              if (isOwned) {
                console.log(`✅ Token #${i} is owned by ${ownerAddress}`);
                const nft = await fetchSingleNFT(contract, i, signal);
                if (nft) return nft;
              }
            } catch (e) {
              console.warn(`Error checking ownership for token #${i}:`, e.message);
              return null;
            }
            return null;
          })()
        );
      }

      const results = await Promise.all(checkPromises);
      console.log(`Checked ${results.length} tokens, found ${results.filter(nft => nft !== null).length} owned by ${ownerAddress}`);

      if (signal.aborted) {
        return;
      }

      const ownedNfts = results.filter(nft => nft !== null).slice(0, limit).reverse();
      console.log('Setting NFTs for My Flights:', ownedNfts);
      setNfts(ownedNfts);
    } catch (err) {
      // Ignore errors from aborted requests
      if (err.name === 'AbortError' || signal.aborted) {
        console.log('Request was cancelled');
        return;
      }

      console.error('Failed to fetch NFTs by owner:', err);
      setError(err.message);
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // Cleanup: cancel any pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    nfts,
    loading,
    error,
    totalSupply,
    fetchNFTs,
    fetchNFTsByOwner,
    refetch: fetchNFTs,
  };
};
